import { createContext, useReducer, useContext, type ReactNode } from 'react';

export type InteractionType = 'thread' | 'comment' | 'reply';

interface OptionDialogState {
  authorId: string;
  contentId: string;
  interaction: InteractionType;
  showDialog: boolean;
}

type OptionDialogAction =
  | {
      type: 'SHOW_DIALOG';
    }
  | {
      type: 'CLOSE_DIALOG';
    };

interface OptionDialogContextType {
  optionDialogState: OptionDialogState;
  optionDialogDispatch: React.Dispatch<OptionDialogAction>;
}

const OptionDialogContext = createContext<OptionDialogContextType | undefined>(
  undefined,
);

interface OptionDialogProviderProps {
  children: ReactNode;
}

const optionDialogReducer = (
  state: OptionDialogState,
  action: OptionDialogAction,
): OptionDialogState => {
  const { type } = action;
  switch (type) {
    case 'SHOW_DIALOG':
      return {
        ...state,
        showDialog: true,
      };
    case 'CLOSE_DIALOG':
      return {
        ...state,
        showDialog: false,
      };
    default:
      throw new Error('groupCardReducer Unknown action type');
  }
};

interface OptionDialogProviderProps {
  children: ReactNode;
  value: { authorId: string; contentId: string; interaction: InteractionType };
}

export const OptionDialogProvider: React.FC<OptionDialogProviderProps> = ({
  children,
  value: { authorId, contentId, interaction },
}: OptionDialogProviderProps) => {
  const [optionDialogState, optionDialogDispatch] = useReducer(
    optionDialogReducer,
    {
      authorId,
      contentId,
      interaction,
      showDialog: false,
    },
  );

  return (
    <OptionDialogContext.Provider
      value={{
        optionDialogState,
        optionDialogDispatch,
      }}
    >
      {children}
    </OptionDialogContext.Provider>
  );
};

export const useOptionDialog = (): OptionDialogContextType => {
  const context = useContext(OptionDialogContext);
  if (!context) {
    throw new Error('useGroupCard must be used within a GroupCardProvider');
  }
  return context;
};

export default OptionDialogProvider;
